/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import forge from "node-forge";

import { Log } from "../logging/Log";
import type { TPinKeyDeriver } from "../types";

/**
 * A parsed `parentalSettings` PIN hash payload.
 */
interface TParsedPinHash {
  /** Expected derived key bytes. */
  hash: Uint8Array;

  /** PBKDF2 iteration count. */
  iterations: number;

  /** Salt bytes. */
  salt: Uint8Array;
}

/**
 * Local PIN verification against the FE PIN hash payload
 * (PBKDF2-HMAC-SHA256). There is no server-side PIN check by design.
 *
 * Accepted payloads: `<base64 salt>$<iterations>$<base64 hash>` (UPC, API
 * contract) and `<base64 salt>$<base64 hash>` (ONB checklist), the latter only
 * when a fallback iteration count is configured (DRD OQ-6).
 *
 * The entered PIN is never logged.
 */
export class PinHashVerifier {
  private static readonly TAG: string = "PinHashVerifier";

  /** Separator between payload parts. */
  private static readonly SEPARATOR: string = "$";

  /** Upper bound on iterations, so a malformed payload cannot hang the tab. */
  public static readonly MAX_ITERATIONS: number = 10000000;

  private constructor() {}

  /**
   * Decode standard or URL-safe base64, with or without padding.
   * @param text - Base64 text
   * @returns Bytes, or null when the text is not valid base64
   */
  private static decodeBase64(text: string): Uint8Array | null {
    const normalized: string = text
      .trim()
      .replaceAll("-", "+")
      .replaceAll("_", "/");
    if (normalized === "" || !/^[A-Za-z0-9+/]*={0,2}$/.test(normalized)) {
      return null;
    }
    const padded: string =
      normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    try {
      const binary: string = atob(padded);
      const bytes: Uint8Array = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.codePointAt(i) || 0;
      }
      return bytes;
    } catch {
      return null;
    }
  }

  /**
   * Parse an iteration count.
   * @param text - Iteration text
   * @returns The count, or null when out of range or not an integer
   */
  private static parseIterations(text: string): number | null {
    if (!/^\d+$/.test(text.trim())) {
      return null;
    }
    const iterations: number = Number(text.trim());
    if (iterations < 1 || iterations > PinHashVerifier.MAX_ITERATIONS) {
      return null;
    }
    return iterations;
  }

  /**
   * Compare two byte arrays in constant time for equal lengths.
   * @param left - First array
   * @param right - Second array
   * @returns True when identical
   */
  private static constantTimeEquals(
    left: Uint8Array,
    right: Uint8Array
  ): boolean {
    if (left.length !== right.length) {
      return false;
    }
    let difference: number = 0;
    for (let i = 0; i < left.length; i++) {
      difference |= left[i] ^ right[i];
    }
    return difference === 0;
  }

  /**
   * Convert bytes to a forge binary string.
   * @param bytes - Bytes
   * @returns Binary string
   */
  private static toBinaryString(bytes: Uint8Array): string {
    let binary: string = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCodePoint(bytes[i]);
    }
    return binary;
  }

  /**
   * PBKDF2-HMAC-SHA256 via WebCrypto, or node-forge where WebCrypto is
   * missing (older TV browsers, non-secure contexts).
   * @param pin - Entered PIN
   * @param salt - Salt bytes
   * @param iterations - Iteration count
   * @param keyLengthBytes - Derived key length
   * @returns Derived key bytes
   */
  public static async deriveKey(
    pin: string,
    salt: Uint8Array,
    iterations: number,
    keyLengthBytes: number
  ): Promise<Uint8Array> {
    const subtle: SubtleCrypto | undefined =
      typeof crypto !== "undefined" && crypto !== null
        ? crypto.subtle
        : undefined;
    if (
      subtle !== undefined &&
      subtle !== null &&
      typeof subtle.importKey === "function" &&
      typeof subtle.deriveBits === "function"
    ) {
      const key: CryptoKey = await subtle.importKey(
        "raw",
        new TextEncoder().encode(pin),
        "PBKDF2",
        false,
        ["deriveBits"]
      );
      const bits: ArrayBuffer = await subtle.deriveBits(
        {
          name: "PBKDF2",
          salt: new Uint8Array(salt),
          iterations,
          hash: "SHA-256"
        },
        key,
        keyLengthBytes * 8
      );
      return new Uint8Array(bits);
    }
    Log.w(PinHashVerifier.TAG, "deriveKey() — WebCrypto missing, using forge");
    const derived: string = forge.pkcs5.pbkdf2(
      pin,
      PinHashVerifier.toBinaryString(salt),
      iterations,
      keyLengthBytes,
      forge.md.sha256.create()
    );
    const bytes: Uint8Array = new Uint8Array(derived.length);
    for (let i = 0; i < derived.length; i++) {
      bytes[i] = derived.codePointAt(i) || 0;
    }
    return bytes;
  }

  /**
   * Parse a PIN hash payload.
   * @param payload - Payload from `parentalSettings`
   * @param iterationsFallback - Iterations for two-part payloads, or null
   * @returns Parsed payload, or null when it cannot be used
   */
  public static parse(
    payload: string,
    iterationsFallback: number | null
  ): TParsedPinHash | null {
    const parts: Array<string> = payload.split(PinHashVerifier.SEPARATOR);
    let saltText: string;
    let hashText: string;
    let iterations: number | null;
    if (parts.length === 3) {
      saltText = parts[0];
      iterations = PinHashVerifier.parseIterations(parts[1]);
      hashText = parts[2];
    } else if (parts.length === 2) {
      saltText = parts[0];
      hashText = parts[1];
      iterations =
        iterationsFallback !== null
          ? PinHashVerifier.parseIterations(String(iterationsFallback))
          : null;
    } else {
      return null;
    }
    const salt: Uint8Array | null = PinHashVerifier.decodeBase64(saltText);
    const hash: Uint8Array | null = PinHashVerifier.decodeBase64(hashText);
    if (iterations === null || salt === null || hash === null) {
      return null;
    }
    if (salt.length === 0 || hash.length === 0) {
      return null;
    }
    return { hash, iterations, salt };
  }

  /**
   * Verify an entered PIN against a payload.
   * @param pin - Entered PIN
   * @param payload - PIN hash payload
   * @param iterationsFallback - Iterations for two-part payloads, or null
   * @param deriver - Key derivation function
   * @returns True on match, false on mismatch, null when the payload is unusable
   */
  public static async verify(
    pin: string,
    payload: string,
    iterationsFallback: number | null,
    deriver: TPinKeyDeriver
  ): Promise<boolean | null> {
    const parsed: TParsedPinHash | null = PinHashVerifier.parse(
      payload,
      iterationsFallback
    );
    if (parsed === null) {
      Log.w(PinHashVerifier.TAG, "verify() — PIN hash payload unusable");
      return null;
    }
    const derived: Uint8Array = await deriver(
      pin,
      parsed.salt,
      parsed.iterations,
      parsed.hash.length
    );
    return PinHashVerifier.constantTimeEquals(derived, parsed.hash);
  }
}
